/** Basic robust statistics over typed arrays (NaN values are ignored). */

export type NumArray = ArrayLike<number> & { length: number };

/** Copy finite values into a new Float64Array. */
export function finiteValues(a: NumArray): Float64Array {
  let n = 0;
  for (let i = 0; i < a.length; i++) if (Number.isFinite(a[i])) n++;
  const out = new Float64Array(n);
  let k = 0;
  for (let i = 0; i < a.length; i++) if (Number.isFinite(a[i])) out[k++] = a[i];
  return out;
}

/** In-place quickselect: returns the k-th smallest element (0-based). */
export function quickselect(a: Float64Array | Float32Array, k: number, lo = 0, hi = a.length - 1): number {
  while (hi > lo) {
    const pivot = a[(lo + hi) >> 1];
    let i = lo;
    let j = hi;
    while (i <= j) {
      while (a[i] < pivot) i++;
      while (a[j] > pivot) j--;
      if (i <= j) {
        const t = a[i];
        a[i] = a[j];
        a[j] = t;
        i++;
        j--;
      }
    }
    if (k <= j) hi = j;
    else if (k >= i) lo = i;
    else return a[k];
  }
  return a[k];
}

/**
 * Percentile (0..100) with linear interpolation between order statistics
 * (the "linear" / type-7 definition used by NumPy and Excel). Mutates `a`.
 */
export function percentileInPlace(a: Float64Array | Float32Array, p: number): number {
  const n = a.length;
  if (n === 0) return NaN;
  const pos = (Math.min(Math.max(p, 0), 100) / 100) * (n - 1);
  const k = Math.floor(pos);
  const lo = quickselect(a, k);
  if (k + 1 >= n || pos === k) return lo;
  // Smallest element above index k lies in the upper partition after quickselect.
  let hi = Infinity;
  for (let i = k + 1; i < n; i++) if (a[i] < hi) hi = a[i];
  return lo + (pos - k) * (hi - lo);
}

export interface BasicStats {
  n: number;
  mean: number;
  sd: number;
  min: number;
  max: number;
  argmin: number;
  argmax: number;
  sum: number;
  rms: number;
}

export function basicStats(a: NumArray): BasicStats {
  let n = 0;
  let sum = 0;
  let sq = 0;
  let min = Infinity;
  let max = -Infinity;
  let argmin = -1;
  let argmax = -1;
  for (let i = 0; i < a.length; i++) {
    const v = a[i];
    if (!Number.isFinite(v)) continue;
    n++;
    sum += v;
    sq += v * v;
    if (v < min) {
      min = v;
      argmin = i;
    }
    if (v > max) {
      max = v;
      argmax = i;
    }
  }
  if (n === 0) return { n, mean: NaN, sd: NaN, min: NaN, max: NaN, argmin, argmax, sum: 0, rms: NaN };
  const mean = sum / n;
  // Two-pass variance for numerical stability.
  let ss = 0;
  for (let i = 0; i < a.length; i++) {
    const v = a[i];
    if (Number.isFinite(v)) ss += (v - mean) ** 2;
  }
  return { n, mean, sd: n > 1 ? Math.sqrt(ss / (n - 1)) : 0, min, max, argmin, argmax, sum, rms: Math.sqrt(sq / n) };
}

export interface Histogram {
  counts: Uint32Array;
  lo: number;
  hi: number;
  bins: number;
  /** Values below lo / above hi. */
  under: number;
  over: number;
  total: number;
  finiteMin: number;
  finiteMax: number;
}

export function histogram(a: NumArray, bins: number, lo?: number, hi?: number): Histogram {
  let fmin = Infinity;
  let fmax = -Infinity;
  for (let i = 0; i < a.length; i++) {
    const v = a[i];
    if (!Number.isFinite(v)) continue;
    if (v < fmin) fmin = v;
    if (v > fmax) fmax = v;
  }
  if (!Number.isFinite(fmin)) {
    fmin = 0;
    fmax = 1;
  }
  const L = lo ?? fmin;
  let Hh = hi ?? fmax;
  if (!(Hh > L)) Hh = L + (Math.abs(L) || 1) * 1e-6;
  const counts = new Uint32Array(bins);
  const k = bins / (Hh - L);
  let under = 0;
  let over = 0;
  let total = 0;
  for (let i = 0; i < a.length; i++) {
    const v = a[i];
    if (!Number.isFinite(v)) continue;
    total++;
    if (v < L) under++;
    else if (v > Hh) over++;
    else counts[Math.min(bins - 1, Math.floor((v - L) * k))]++;
  }
  return { counts, lo: L, hi: Hh, bins, under, over, total, finiteMin: fmin, finiteMax: fmax };
}

/** Approximate percentile from a histogram (linear within the bin). */
export function histogramPercentile(h: Histogram, p: number): number {
  const target = (p / 100) * h.total;
  let acc = h.under;
  if (target <= acc) return h.lo;
  const bw = (h.hi - h.lo) / h.bins;
  for (let i = 0; i < h.bins; i++) {
    const c = h.counts[i];
    if (acc + c >= target) return h.lo + (i + (c ? (target - acc) / c : 0)) * bw;
    acc += c;
  }
  return h.hi;
}

/**
 * Percentile of all finite values via histograms, refining the bin that holds
 * the percentile until it is resolved (robust against extreme outliers).
 */
export function histPercentile(a: NumArray, p: number, bins = 4096): number {
  let h = histogram(a, bins);
  let v = histogramPercentile(h, p);
  for (let iter = 0; iter < 4; iter++) {
    const bw = (h.hi - h.lo) / h.bins;
    const b = Math.min(h.bins - 1, Math.max(0, Math.floor((v - h.lo) / bw)));
    // Stop once the containing bin holds a negligible share of the samples.
    if (h.counts[b] <= Math.max(1, h.total * 1e-4)) break;
    const lo = h.lo + b * bw;
    const hi = lo + bw;
    if (!(hi > lo)) break;
    h = histogram(a, bins, lo, hi);
    v = histogramPercentile(h, p);
  }
  return v;
}

/** Fast robust display range: percentiles p and 100-p of all finite values. */
export function robustRange(a: NumArray, p = 0.1): [number, number] {
  let lo = histPercentile(a, p);
  let hi = histPercentile(a, 100 - p);
  if (!(hi > lo)) {
    const h = histogram(a, 2);
    lo = h.finiteMin;
    hi = h.finiteMax > h.finiteMin ? h.finiteMax : h.finiteMin + 1;
  }
  return [lo, hi];
}
